import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Space, Card, Typography, Alert } from 'antd';
import { useChannelStore } from '../../store/channelStore';
import { ChannelFormData, NotificationChannel, ChannelPlatform } from '../../types/auth';

const { Title, Text, Paragraph } = Typography;

/**
 * 新建/编辑渠道模态框
 * 支持动态表单和不同平台的凭证配置
 */
const ChannelFormModal: React.FC = () => {
  const {
    isFormModalVisible,
    editingChannel,
    loading,
    createChannel,
    updateChannel,
    closeFormModal,
  } = useChannelStore();

  const [form] = Form.useForm();
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');

  // 支持的平台配置
  const platforms: ChannelPlatform[] = [
    {
      type: 'TELEGRAM',
      name: 'Telegram',
      icon: '📱',
      description: '通过 Telegram Bot 发送通知',
      credentialFields: [
        {
          key: 'bot_token',
          label: 'Bot Token',
          type: 'password',
          required: true,
          placeholder: '请输入 Bot Token',
          helpText: '从 @BotFather 获取 Bot Token',
        },
        {
          key: 'chat_id',
          label: 'Chat ID',
          type: 'input',
          required: true,
          placeholder: '请输入 Chat ID',
          helpText: '群组或私聊的 Chat ID，可通过 @userinfobot 获取',
        },
      ],
    },
    {
      type: 'DISCORD',
      name: 'Discord',
      icon: '🎮',
      description: '通过 Discord Webhook 发送通知',
      credentialFields: [
        {
          key: 'webhook_url',
          label: 'Webhook URL',
          type: 'input',
          required: true,
          placeholder: 'https://discord.com/api/webhooks/...',
          helpText: '在 Discord 频道设置中创建 Webhook',
          validation: {
            pattern: /^https:\/\/discord\.com\/api\/webhooks\/.+/,
            message: '请输入有效的 Discord Webhook URL',
          },
        },
      ],
    },
    {
      type: 'DINGTALK',
      name: '钉钉',
      icon: '💼',
      description: '通过钉钉群机器人发送通知',
      credentialFields: [
        {
          key: 'webhook_url',
          label: 'Webhook URL',
          type: 'input',
          required: true,
          placeholder: 'https://oapi.dingtalk.com/robot/send?access_token=...',
          helpText: '在钉钉群中添加自定义机器人获取 Webhook URL',
          validation: {
            pattern: /^https:\/\/oapi\.dingtalk\.com\/robot\/send\?access_token=.+/,
            message: '请输入有效的钉钉 Webhook URL',
          },
        },
      ],
    },
    {
      type: 'FEISHU',
      name: '飞书',
      icon: '🚀',
      description: '通过飞书群机器人发送通知',
      credentialFields: [
        {
          key: 'webhook_url',
          label: 'Webhook URL',
          type: 'input',
          required: true,
          placeholder: 'https://open.feishu.cn/open-apis/bot/v2/hook/...',
          helpText: '在飞书群中添加自定义机器人获取 Webhook URL',
          validation: {
            pattern: /^https:\/\/open\.feishu\.cn\/open-apis\/bot\/v2\/hook\/.+/,
            message: '请输入有效的飞书 Webhook URL',
          },
        },
      ],
    },
    {
      type: 'EMAIL',
      name: '邮件',
      icon: '📧',
      description: '通过邮件发送通知',
      credentialFields: [
        {
          key: 'to',
          label: '收件人邮箱',
          type: 'input',
          required: true,
          placeholder: 'user@example.com',
          helpText: '接收通知的邮箱地址',
          validation: {
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: '请输入有效的邮箱地址',
          },
        },
        {
          key: 'from',
          label: '发件人邮箱',
          type: 'input',
          required: true,
          placeholder: 'noreply@example.com',
          helpText: '发送通知的邮箱地址',
          validation: {
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: '请输入有效的邮箱地址',
          },
        },
      ],
    },
    {
      type: 'BARK',
      name: 'Bark',
      icon: '🐕',
      description: '通过 Bark 发送 iOS 推送通知',
      credentialFields: [
        {
          key: 'webhook_url',
          label: 'Bark URL',
          type: 'input',
          required: true,
          placeholder: 'https://api.day.app/your_key/',
          helpText: '从 Bark App 获取推送 URL',
          validation: {
            pattern: /^https:\/\/api\.day\.app\/.+/,
            message: '请输入有效的 Bark URL',
          },
        },
      ],
    },
    {
      type: 'WEBHOOK',
      name: '通用 Webhook',
      icon: '🔗',
      description: '通过自定义 Webhook 发送通知',
      credentialFields: [
        {
          key: 'webhook_url',
          label: 'Webhook URL',
          type: 'input',
          required: true,
          placeholder: 'https://your-api.com/webhook',
          helpText: '自定义的 Webhook 接收地址',
          validation: {
            pattern: /^https?:\/\/.+/,
            message: '请输入有效的 URL',
          },
        },
      ],
    },
  ];

  // 初始化表单数据
  useEffect(() => {
    if (editingChannel) {
      setSelectedPlatform(editingChannel.channel_type);
      form.setFieldsValue({
        channel_name: editingChannel.channel_name,
        channel_type: editingChannel.channel_type,
        ...editingChannel.credentials,
      });
    } else {
      form.resetFields();
      setSelectedPlatform('');
    }
  }, [editingChannel, form]);

  // 处理平台选择
  const handlePlatformSelect = (platformType: string) => {
    setSelectedPlatform(platformType);
    form.setFieldValue('channel_type', platformType);
    // 清空凭证字段
    const platform = platforms.find(p => p.type === platformType);
    if (platform) {
      const credentialFields: Record<string, any> = {};
      platform.credentialFields.forEach(field => {
        credentialFields[field.key] = undefined;
      });
      form.setFieldsValue(credentialFields);
    }
  };

  // 处理表单提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // 构建凭证对象
      const platform = platforms.find(p => p.type === selectedPlatform);
      const credentials: Record<string, any> = {};
      
      if (platform) {
        platform.credentialFields.forEach(field => {
          if (values[field.key]) {
            credentials[field.key] = values[field.key];
          }
        });
      }

      const formData: ChannelFormData = {
        channel_name: values.channel_name,
        channel_type: selectedPlatform as any,
        credentials,
      };

      let success = false;
      if (editingChannel) {
        success = await updateChannel(editingChannel.id, formData);
      } else {
        success = await createChannel(formData);
      }

      if (success) {
        closeFormModal();
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  // 处理取消
  const handleCancel = () => {
    closeFormModal();
  };

  // 获取当前选中的平台配置
  const currentPlatform = platforms.find(p => p.type === selectedPlatform);

  return (
    <Modal
      title={editingChannel ? '编辑通知渠道' : '新建通知渠道'}
      open={isFormModalVisible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          取消
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          disabled={!selectedPlatform}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
          }}
        >
          {editingChannel ? '更新' : '创建'}
        </Button>,
      ]}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        className="space-y-6"
      >
        {/* 第一步：选择通知平台 */}
        <div>
          <Title level={4}>1. 选择通知平台</Title>
          <div className="flex flex-wrap gap-2 mb-4">
            {platforms.map((platform) => (
              <Button
                key={platform.type}
                type={selectedPlatform === platform.type ? 'primary' : 'default'}
                onClick={() => handlePlatformSelect(platform.type)}
                className={`flex items-center gap-2 ${
                  selectedPlatform === platform.type
                    ? 'bg-blue-500 border-blue-500'
                    : 'hover:border-blue-300'
                }`}
                style={selectedPlatform === platform.type ? {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                } : {}}
              >
                <span>{platform.icon}</span>
                <span>{platform.name}</span>
              </Button>
            ))}
          </div>
          {selectedPlatform && currentPlatform && (
            <Alert
              message={currentPlatform.description}
              type="info"
              showIcon
              className="mb-4"
            />
          )}
        </div>

        {/* 第二步：填写通用信息 */}
        {selectedPlatform && (
          <div>
            <Title level={4}>2. 填写基本信息</Title>
            <Form.Item
              name="channel_name"
              label="通知名称"
              rules={[
                { required: true, message: '请输入通知名称' },
                { max: 50, message: '通知名称不能超过50个字符' },
              ]}
            >
              <Input placeholder="为这个通知渠道起个名字" />
            </Form.Item>
          </div>
        )}

        {/* 第三步：填写凭证信息 */}
        {selectedPlatform && currentPlatform && (
          <div>
            <Title level={4}>3. 配置凭证信息</Title>
            {currentPlatform.credentialFields.map((field) => (
              <Form.Item
                key={field.key}
                name={field.key}
                label={field.label}
                help={field.helpText}
                rules={[
                  { required: field.required, message: `请输入${field.label}` },
                  ...(field.validation ? [field.validation] : []),
                ]}
              >
                {field.type === 'password' ? (
                  <Input.Password placeholder={field.placeholder} />
                ) : field.type === 'textarea' ? (
                  <Input.TextArea 
                    placeholder={field.placeholder} 
                    rows={3}
                  />
                ) : (
                  <Input placeholder={field.placeholder} />
                )}
              </Form.Item>
            ))}
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default ChannelFormModal;
